'use client';

/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
import { FeCarouselV2 } from '../FeCarousel';
import { MaterialIcon } from '../MaterialIcon';
import RewardGridCard, { RewardGridCardProps } from '../RewardGridCard';

export type RewardCardGridLayoutType = 'GRID' | 'CAROUSEL';

type RewardCardGridProps = {
  items: RewardGridCardProps[];
  layoutType?: RewardCardGridLayoutType;
};

export const REWARD_CARD_GRID_GRID_COLUMNS = {
  base: 2,
  md: 3,
  lg: 4,
} as const;

export const REWARD_CARD_GRID_CAROUSEL_COLUMNS = {
  base: 2,
  md: 3,
  lg: 4,
} as const;

export const REWARD_CARD_GRID_CAROUSEL_BREAKPOINTS = {
  md: 768,
  lg: 960,
} as const;

export const getRewardCardGridItemsPerRow = ({
  layoutType,
  viewportWidth,
}: {
  layoutType: RewardCardGridLayoutType;
  viewportWidth: number;
}) => {
  if (layoutType === 'CAROUSEL') {
    if (viewportWidth >= REWARD_CARD_GRID_CAROUSEL_BREAKPOINTS.lg) {
      return REWARD_CARD_GRID_CAROUSEL_COLUMNS.lg;
    }
    if (viewportWidth >= REWARD_CARD_GRID_CAROUSEL_BREAKPOINTS.md) {
      return REWARD_CARD_GRID_CAROUSEL_COLUMNS.md;
    }
    return REWARD_CARD_GRID_CAROUSEL_COLUMNS.base;
  }

  if (viewportWidth >= 1024) {
    return REWARD_CARD_GRID_GRID_COLUMNS.lg;
  }
  if (viewportWidth >= REWARD_CARD_GRID_CAROUSEL_BREAKPOINTS.md) {
    return REWARD_CARD_GRID_GRID_COLUMNS.md;
  }
  return REWARD_CARD_GRID_GRID_COLUMNS.base;
};

const RewardCardGridNavigationButton = ({
  icon,
}: {
  icon: 'arrow_left_alt' | 'arrow_right_alt';
}) => (
  <div className="flex size-[30px] items-center justify-center rounded-[15px] bg-fill/card shadow-md">
    <MaterialIcon icon={icon} px={20} className="text-current" />
  </div>
);

const RewardCardGridCard = ({ item }: { item: RewardGridCardProps }) => (
  <RewardGridCard {...item} className="!h-full !w-full sm:!w-full xl:!w-full" />
);

const RewardCardGridList = ({ items }: { items: RewardGridCardProps[] }) => (
  <div className="grid grid-cols-2 gap-x-3 gap-y-spacing/gap/y-sm md:grid-cols-3 lg:grid-cols-4 lg:gap-x-4">
    {items.map((item, index) => (
      <RewardCardGridCard
        key={item.id ?? item.title ?? `reward-card-grid-item-${index}`}
        item={item}
      />
    ))}
  </div>
);

const RewardCardGridCarousel = ({
  items,
  showNavigation,
  shouldAutoplay,
}: {
  items: RewardGridCardProps[];
  showNavigation: boolean;
  shouldAutoplay: boolean;
}) => (
  <div className="relative">
    <FeCarouselV2
      autoplay={
        shouldAutoplay
          ? {
              delay: 2500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false
      }
      navigation={
        showNavigation
          ? {
              showOnDesktop: true,
              showOnMobile: true,
              prevElClassName: '!left-[-22px] lg:!left-[-34px]',
              nextElClassName: '!right-[-22px] lg:!right-[-34px]',
              disabledClassName: '!text-text/disabled',
              prevElement: (
                <RewardCardGridNavigationButton icon="arrow_left_alt" />
              ),
              nextElement: (
                <RewardCardGridNavigationButton icon="arrow_right_alt" />
              ),
            }
          : undefined
      }
      beforeInitSlideClassName="mr-3 w-[calc((100%-24px)/2)] md:w-[calc((100%-36px)/3)] lg:mr-4 lg:w-[calc((100%-64px)/4)]"
      slideClassName="!flex !h-auto"
      slidesPerView={REWARD_CARD_GRID_CAROUSEL_COLUMNS.base}
      slidesPerViewBreakpoints={[
        [
          REWARD_CARD_GRID_CAROUSEL_COLUMNS.md,
          REWARD_CARD_GRID_CAROUSEL_BREAKPOINTS.md,
        ],
        [
          REWARD_CARD_GRID_CAROUSEL_COLUMNS.lg,
          REWARD_CARD_GRID_CAROUSEL_BREAKPOINTS.lg,
        ],
      ]}
      spaceBetween={12}
      spaceBetweenBreakpoints={[[16, REWARD_CARD_GRID_CAROUSEL_BREAKPOINTS.lg]]}
    >
      {items.map((item, index) => (
        <RewardCardGridCard
          key={item.id ?? item.title ?? `reward-card-grid-slide-${index}`}
          item={item}
        />
      ))}
    </FeCarouselV2>
  </div>
);

const RewardCardGrid: React.FC<RewardCardGridProps> = ({
  items,
  layoutType = 'GRID',
}) => {
  const isCarouselLayout = layoutType === 'CAROUSEL';
  const showCarouselNavigation = isCarouselLayout && items.length > 2;
  const shouldAutoplayCarousel = isCarouselLayout && items.length > 2;

  if (!items.length) {
    return null;
  }

  return (
    <section
      className="overflow-hidden w-[calc(100%+var(--spacing-padding-section-x)*2)] px-spacing/padding/section-x -mx-spacing/padding/section-x"
      data-layout-type={layoutType.toLowerCase()}
    >
      {isCarouselLayout ? (
        <RewardCardGridCarousel
          items={items}
          showNavigation={showCarouselNavigation}
          shouldAutoplay={shouldAutoplayCarousel}
        />
      ) : (
        <RewardCardGridList items={items} />
      )}
    </section>
  );
};

export default RewardCardGrid;
